import { computed, getCurrentInstance } from 'vue';
import { useState } from '#imports';

export interface PageHeading {
  /** App-bar title — usually the page's useHead title (e.g. "レベニューセンター一覧"). */
  title: string;
  /** Optional parent context shown small next to the title (e.g. resource name on new/edit). */
  parent?: string;
  /** Docs path for contextual Help shown next to the title in the app bar. */
  helpPath?: string;
}

/** Internal payload; `owner` is the Vue instance uid that published it. */
type PageHeadingState = PageHeading & { owner?: number };

/**
 * Shared page heading, rendered by AdminHeader in the 64px top bar.
 * PageContent publishes title + helpPath here so the app bar can show them.
 */
export const usePageHeading = () => {
  const state = useState<PageHeadingState | null>('page-heading', () => null);

  const setPageHeading = (value: PageHeading | null, owner?: number) => {
    if (value === null) {
      state.value = null;
      return;
    }
    state.value = { ...value, owner };
  };

  /** Clear only if this owner still holds the slot (avoids nav race with the next page). */
  const clearPageHeading = (owner: number) => {
    if (state.value?.owner === owner) {
      state.value = null;
    }
  };

  return {
    heading: computed<PageHeading | null>(() => {
      if (!state.value) return null;
      const { owner: _owner, ...rest } = state.value;
      return rest;
    }),
    setPageHeading,
    clearPageHeading,
  };
};

/** Stable owner id for the calling component (PageContent). */
export const usePageHeadingOwner = () => getCurrentInstance()?.uid ?? 0;
